
import React from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { useAuth } from '@/contexts/AuthContext';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Activity, User, Clock } from 'lucide-react';

const DataEntryDebugPanel: React.FC = () => {
  const { user } = useAuth();

  // Get recent entries for current user
  const { data: recentEntries, isLoading } = useQuery({
    queryKey: ['debug-recent-entries', user?.id],
    queryFn: async () => {
      if (!user?.id) return [];

      console.log('Debug panel: Fetching recent entries for user:', user.id);

      // Query all price tables for recent entries by this user
      const promises = [
        supabase.from('farm_prices').select('id, product_name, price, created_at').eq('created_by', user.id).order('created_at', { ascending: false }).limit(5),
        supabase.from('distribution_center_prices').select('id, product_name, price, created_at').eq('created_by', user.id).order('created_at', { ascending: false }).limit(5),
        supabase.from('local_shop_prices').select('id, product_name, price, created_at').eq('created_by', user.id).order('created_at', { ascending: false }).limit(5),
        supabase.from('sunday_market_prices').select('id, product_name, price, created_at').eq('created_by', user.id).order('created_at', { ascending: false }).limit(5),
        supabase.from('supermarket_prices').select('id, product_name, price, created_at').eq('created_by', user.id).order('created_at', { ascending: false }).limit(5),
        supabase.from('ecommerce_prices').select('id, product_name, price, created_at').eq('created_by', user.id).order('created_at', { ascending: false }).limit(5)
      ];

      const results = await Promise.all(promises);
      const tableNames = ['farm_prices', 'distribution_center_prices', 'local_shop_prices', 'sunday_market_prices', 'supermarket_prices', 'ecommerce_prices'];
      
      const allEntries = results.flatMap((result, index) => 
        (result.data || []).map(entry => ({
          ...entry,
          table: tableNames[index]
        }))
      );

      // Sort by created_at and take top 10
      const sortedEntries = allEntries.sort((a, b) => 
        new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
      ).slice(0, 10);

      console.log('Debug panel: Found entries:', sortedEntries);
      return sortedEntries;
    },
    enabled: !!user?.id,
    refetchInterval: 10000, // Refresh every 10 seconds
    refetchIntervalInBackground: false,
  });

  if (!user) {
    return (
      <Card className="bg-yellow-50 border-yellow-200">
        <CardContent className="pt-6">
          <p className="text-sm text-yellow-800">No user authentication found</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="bg-blue-50 border-blue-200">
      <CardHeader className="pb-3">
        <CardTitle className="text-sm flex items-center gap-2">
          <Activity className="h-4 w-4" />
          Data Entry Debug Panel
        </CardTitle>
        <CardDescription className="text-xs">
          Real-time tracking of your price data entries
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="flex items-center gap-2 text-sm">
          <User className="h-3 w-3" />
          <span className="font-medium">Current User:</span>
          <Badge variant="outline">{user.email}</Badge>
          <Badge variant="secondary">{user.role}</Badge>
        </div>
        
        <div className="text-xs text-blue-800">
          <strong>User ID:</strong> {user.id}
        </div>

        <div className="space-y-2">
          <div className="flex items-center gap-2 text-sm font-medium">
            <Clock className="h-3 w-3" />
            Recent Entries ({recentEntries?.length || 0})
          </div>
          
          {isLoading ? (
            <div className="text-xs text-blue-600">Loading recent entries...</div>
          ) : recentEntries && recentEntries.length > 0 ? (
            <div className="space-y-1 max-h-32 overflow-y-auto">
              {recentEntries.map((entry: any, index) => (
                <div key={`${entry.table}-${entry.id}`} className="text-xs bg-white p-2 rounded border">
                  <div className="flex justify-between items-start">
                    <div>
                      <div className="font-medium">{entry.product_name}</div>
                      <div className="text-gray-600">{entry.price} ETB/kg</div>
                    </div>
                    <div className="text-right">
                      <Badge variant="outline" className="text-xs">
                        {entry.table.replace('_prices', '').replace('_', ' ')}
                      </Badge>
                      <div className="text-gray-500 mt-1">
                        {new Date(entry.created_at).toLocaleString()}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-xs text-gray-600 italic">
              No entries found for this user yet
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

export default DataEntryDebugPanel;
